'use client';

import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Alert, AlertDescription } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@kit/ui/input-otp';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import type { GuestOwnershipChallenge } from '../lib/guest-ownership';

const RESEND_COOLDOWN_SECONDS = 30;

const OwnershipCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/),
});

/**
 * Web step shown when a sign-up matched an existing guest profile (created by
 * the business or over WhatsApp). The server sent a code to the WhatsApp
 * number or email already on file; entering it proves the profile is the
 * customer's before anything on it changes. Submitting replays the original
 * sign-up with the code — the server verifies it.
 */
export function GuestOwnershipCodeStep({
  challenge,
  loading,
  onSubmit,
  onResend,
  onCancel,
}: {
  challenge: GuestOwnershipChallenge;
  loading: boolean;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onCancel: () => void;
}) {
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  // Countdown timer for the resend button (same pattern as the signup
  // verification code form).
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const form = useForm<z.infer<typeof OwnershipCodeSchema>>({
    resolver: zodResolver(OwnershipCodeSchema),
    defaultValues: { code: '' },
  });

  const handleResend = () => {
    form.setValue('code', '');
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    onResend();
  };

  return (
    <Form {...form}>
      <form
        className="flex w-full flex-col items-center space-y-6"
        onSubmit={form.handleSubmit(({ code }) => onSubmit(code))}
        data-test="guest-ownership-step"
      >
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-foreground font-medium">
            <Trans
              i18nKey="auth:ownershipHeading"
              defaults="Confirm it's you"
            />
          </p>

          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey={
                challenge.channel === 'whatsapp'
                  ? 'auth:ownershipBodyWhatsapp'
                  : 'auth:ownershipBodyEmail'
              }
              values={{ hint: challenge.hint }}
              components={{ strong: <strong className="text-foreground" /> }}
            />
          </p>
        </div>

        {challenge.invalid ? (
          <Alert variant="destructive">
            <AlertDescription data-test="guest-ownership-invalid">
              <Trans i18nKey="auth:ownershipInvalidCode" />
            </AlertDescription>
          </Alert>
        ) : null}

        <FormField
          name="code"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  disabled={loading}
                  data-test="guest-ownership-code-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex w-full flex-col gap-y-2">
          <Button
            type="submit"
            disabled={loading}
            data-test="guest-ownership-verify-button"
          >
            {loading ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                <Trans i18nKey="common:otp.verifying" />
              </>
            ) : (
              <Trans i18nKey="common:otp.verifyCode" />
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            disabled={loading || resendCooldown > 0}
            onClick={handleResend}
            data-test="guest-ownership-resend-button"
          >
            {resendCooldown > 0 ? (
              <Trans
                i18nKey="common:otp.resendCodeIn"
                values={{ seconds: resendCooldown }}
              />
            ) : (
              <Trans i18nKey="common:otp.requestNewCode" />
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            disabled={loading}
            onClick={onCancel}
          >
            <Trans i18nKey="common:cancel" defaults="Cancel" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
